import React, { useState, useEffect } from 'react'

import i18n from 'live/services/i18n'

import { IAlarp, IBarrierStatusSpecialStatus } from 'plugin-codaf-risk-ext-bowtie/types'
import { truncate } from 'plugin-codaf-risk-ext-bowtie/utils/truncate'

import { useBowtieStated } from '../../../../../../../../../hooks'
import {
    getTopEventStatusColor,
    getTopEventStatusIcon,
    getTopEventStatusName,
    getAlarpStatusByName,
    globalComponentsIndex
} from '../../../../../../../../../utils'
import { bowtieTheme } from '../../../../../../../../../utils/constants'
import { BowtieView } from '../../../../../../../../BowtieViews'
import { IBarrierCount } from '../../../../../../AssetSelector'
import { mapJoin } from '../../../../AssetCard'
import { IActiveInactive } from '../../OperationItem'
import { OperationBowtieItemStyled, StyledCustomTooltip } from './styled'

interface IProps {
    bowtieView: BowtieView
    onSelectBowtie: () => void
    bowtieStatusCount: IBarrierCount
    alarpList: IAlarp[]
    inactiveStatus: IBarrierStatusSpecialStatus
    activeStatus: IBarrierStatusSpecialStatus
}

export const OperationBowtieItem: React.FC<IProps> = ({
    bowtieView,
    onSelectBowtie,
    bowtieStatusCount,
    alarpList,
    activeStatus,
    inactiveStatus
}) => {
    const [totalPrevention, setTotalPrevention] = useState<IActiveInactive>({ total: 0, active: 0, inactive: 0 })
    const [totalMitigation, setTotalMitigation] = useState<IActiveInactive>({ total: 0, active: 0, inactive: 0 })

    const index: number = globalComponentsIndex.incrementAndGet()
    const event = useBowtieStated(bowtieView.topEventView)

    if (bowtieView.bowtie.methodType === 'SEMI_QUALITATIVE') bowtieView.bowtie.methodType = 'QUALITATIVE'

    const functionGet = getAlarpStatusByName(
        alarpList.find(alarp => alarp.methodTypeName === bowtieView.bowtie.methodType).alarpThresholds
    )

    const getActualColor = (actualCount: number, total: number): string => {
        const percent = actualCount / total
        if (percent >= 0 && percent < 0.15) {
            return bowtieTheme.barrierColors.active
        } else if (percent >= 0.15 && percent <= 0.5) {
            return bowtieTheme.barrierColors.degraded
        } else {
            return bowtieTheme.barrierColors.inactive
        }
    }

    function getTotalBarriers(): void {
        const itemsPrevention: IActiveInactive = { total: 0, active: 0, inactive: 0 }
        const itemsMitigation: IActiveInactive = { total: 0, active: 0, inactive: 0 }

        itemsPrevention.total = itemsPrevention.total + mapJoin(bowtieStatusCount.prevention)
        itemsPrevention.active = itemsPrevention.active + (bowtieStatusCount.prevention.get(activeStatus.id) ?? 0)
        itemsPrevention.inactive = itemsPrevention.inactive + (bowtieStatusCount.prevention.get(inactiveStatus.id) ?? 0)

        itemsMitigation.total = itemsMitigation.total + mapJoin(bowtieStatusCount.mitigation)
        itemsMitigation.active = itemsMitigation.active + (bowtieStatusCount.mitigation.get(activeStatus.id) ?? 0)
        itemsMitigation.inactive = itemsMitigation.inactive + (bowtieStatusCount.mitigation.get(inactiveStatus.id) ?? 0)

        setTotalPrevention(itemsPrevention)
        setTotalMitigation(itemsMitigation)
    }

    useEffect(() => {
        getTotalBarriers()
    }, [bowtieStatusCount]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <OperationBowtieItemStyled
            className={`export-pdf operation-bowtie-item-styled ${index % 11 === 0 ? 'divider-pdf' : ''}`}
        >
            <StyledCustomTooltip
                content={`${bowtieView.bowtie.topEvent.hazard.name} | ${truncate(bowtieView.bowtie.topEvent.name, 30)}`}
                position="top"
            >
                <div className="export-pdf operation-bowtie-details" onClick={onSelectBowtie}>
                    <div className="export-pdf operation-bowtie-name">{bowtieView.bowtie.bowtieCode}</div>
                    <div className="export-pdf operation-bowtie-description">{bowtieView.bowtie.topEvent.name} </div>
                </div>
            </StyledCustomTooltip>
            <div
                className="export-pdf operation-bowtie-status"
                style={{ color: getTopEventStatusColor(event?.value, functionGet) }}
            >
                {getTopEventStatusIcon(event?.value, functionGet)}{' '}
                {getTopEventStatusName(event?.value, i18n, functionGet)}
            </div>
            <div className="operation-bowtie-barrier-count prevention export-pdf number-count-bowtie">
                <span
                    className="export-pdf number-count-bowtie"
                    style={{
                        color: getActualColor(totalPrevention.total - totalPrevention.active, totalPrevention.total)
                    }}
                >
                    {totalPrevention.total - totalPrevention.active}
                </span>
                /{totalPrevention.total}
            </div>
            <div className="operation-bowtie-barrier-count mitigation export-pdf number-count-bowtie">
                <span
                    className="export-pdf number-count-bowtie"
                    style={{
                        color: getActualColor(totalMitigation.total - totalMitigation.active, totalMitigation.total)
                    }}
                >
                    {totalMitigation.total - totalMitigation.active}
                </span>
                /{totalMitigation.total}
            </div>
        </OperationBowtieItemStyled>
    )
}
